import { createSkillRequirementChecker } from './skill-requirements';
import type { ItemFieldRow } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readInventoryField, readLayerListEntryUuid } from './inventory-raw-readers';
import { readCreatureStatuses } from './creature-status-reader';
import { phpJsonInt, phpJsonString, phpJsonTruthy, comparePhpStrings } from './php-json-values';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';

const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, k: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k) ? (v as Record<string, unknown>)[k] : undefined;
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
// Native == is deliberate in equipment/environment gates. JSON objects model PHP arrays.
function equal(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (typeof a === 'boolean' || typeof b === 'boolean') return phpJsonTruthy(a) === phpJsonTruthy(b);
    if (a == null || b == null) {
        const other = a == null ? b : a;
        return typeof other === 'string' ? other === '' : !phpJsonTruthy(other);
    }
    if (arrayLike(a) || arrayLike(b)) return arrayLike(a) && arrayLike(b) && Object.keys(a).length === Object.keys(b).length
        && Object.entries(a).every(([k, v]) => Object.prototype.hasOwnProperty.call(b, k) && equal(v, own(b, k)));
    if (isPhpNumeric(a) && isPhpNumeric(b)) {
        if (typeof a === 'string' && typeof b === 'string') return comparePhpStrings(a, b) === 0;
        return Number(a) === Number(b);
    }
    return phpJsonString(a) === phpJsonString(b);
}
export type AbilityConditionSource = 'present-light' | 'effective-light' | 'outdoors' | 'sunlight' | 'summon-within' | 'equipped-items' | 'draw-sheathed' | 'skills' | 'median-charge';
/** Gates this evaluator actually answers. Consumers must not admit arbitrary `if_*` layers as evaluated. */
const CONDITION_LAYERS = new Set([
    ...['armor_type_equipped', 'weapon_classification_equipped', 'weapon_type_equipped', 'weapon_has_property_equipped',
        'caster_has_status_effect', 'light_type', 'environment', 'weather', 'effective_light_type', 'outdoors', 'sunlight']
        .flatMap(family => [`if_${family}_active`, `if_${family}_not_active`]),
    'if_caster_summon_within_ft', 'requires_equipped_item', 'draw_sheathed_weapon_for_attack_ability', '_draw_bound_inventory_id',
    'ability_active_if_ability_charge_is_at_median',
]);
export const isAbilityConditionLayer = (name: string): boolean => CONDITION_LAYERS.has(name) || name.startsWith('requires_skill_tier_');
export interface AbilityConditionDependencies extends FormulaReadDependencies {
    /** Live consumers already hold the page readers, including thin inventory / catalog bridges. */
    layerReaders?: Pick<ReturnType<typeof createFormulaReadContext>, 'layerValue' | 'layerEntries'>;
    /** When supplied, skill gates run locally; omitted registries retain the explicit callee boundary. */
    skillEntries?: readonly {item_id: string; variable_name: string}[];
    equipmentSlots: Readonly<Record<'armor' | 'main_hand' | 'off_hand' | 'both_hands', string>>;
    vitalityStatusIds: readonly string[];
    /** Actual contextual helper answer. Missing inputs must throw, never become true. */
    conditionSource: (kind: AbilityConditionSource, args: readonly unknown[]) => unknown;
}
/** Native gate ordering includes assign-style families followed by AND-style
 * families. Preserve that behavior; turning the whole function into an AND
 * would change existing PHP semantics. No condition result is memoized here.
 */
export function createAbilityConditionEvaluator(deps: AbilityConditionDependencies) {
    const emit = (e: readonly unknown[]): void => { deps.trace?.(e); };
    const readers = { ...createFormulaReadContext(deps), ...deps.layerReaders };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const providers = createCatalogDefaultProviderReader({catalogRows: deps.catalogRows, nodeItemId: readNodeItemId, resolveCarrierRef: reference});
    const has = (row: ItemFieldRow, name: string): boolean => readers.layerValue(row, name) !== null || phpJsonTruthy(readers.layerEntries(row, name));
    const entries = (row: ItemFieldRow, name: string): unknown[] => Object.values(readers.layerEntries(row, name));
    const skills = deps.skillEntries === undefined ? null : createSkillRequirementChecker({...deps, skillEntries: deps.skillEntries,
        ...(deps.layerReaders ? { layerEntries: (row, key) => Object.values(deps.layerReaders!.layerEntries(row, key)) } : {}) });
    const source = (kind: AbilityConditionSource, args: readonly unknown[]): unknown => kind === 'skills' && skills
        ? skills(args[0] as ItemFieldRow, args[1] as ItemFieldRow) : deps.conditionSource(kind, args);
    function hasRuntimeConditions(row: ItemFieldRow): boolean {
        if (!arrayLike(row)) throw new TypeError('Runtime condition preflight requires a PHP array');
        const visited = new Set<string>();
        function walk(node: ItemFieldRow): boolean {
            const id = readNodeItemId(node);
            if (id !== null) { const key = id.toUpperCase(); if (visited.has(key)) return false; visited.add(key); }
            const layers = own(node, 'item_layer_types');
            for (const surface of [...(arrayLike(layers) ? [layers] : []), node]) for (const [key, value] of Object.entries(surface)) {
                if (value === null || value === '' || (arrayLike(value) && Object.keys(value).length === 0)) continue;
                if (key.startsWith('if_') || key.startsWith('requires_skill_tier_') || ['requires_equipped_item', 'draw_sheathed_weapon_for_attack_ability', 'ability_active_if_ability_charge_is_at_median'].includes(key)) return true;
            }
            for (const carrier of providers.providers(node)) if (arrayLike(carrier) && walk(carrier)) return true;
            return false;
        }
        return walk(row);
    }
    function check(ability: ItemFieldRow | null, host: ItemFieldRow, template = false, deferSkill = false): boolean {
        if (!arrayLike(host) || (ability !== null && !arrayLike(ability))) throw new TypeError('Condition gate requires PHP arrays');
        if (ability === null) { readers.itemField(host, 'item_id'); return false; }
        if (!hasRuntimeConditions(ability)) return true;
        let met = true;
        const equipped = (row: ItemFieldRow): unknown => readInventoryField(row, 'equipped') ?? '';
        const hands = (row: ItemFieldRow): boolean => ['main_hand', 'off_hand', 'both_hands'].some(slot => equal(equipped(row), deps.equipmentSlots[slot as 'main_hand']));
        for (const family of ['armor_type', 'weapon_classification', 'weapon_type', 'weapon_has_property']) for (const negative of [false, true]) {
            const key = 'if_' + family + '_equipped_' + (negative ? 'not_active' : 'active');
            if (!has(ability, key) || (template && !(family === 'armor_type' && !negative))) continue;
            met = negative;
            // check_ability_conditions.php:218 / 266 / 315 / 365 — a weapon_type AND a weapon_classification requirement read as the bare UUID
            // (layer_entries can yield bucketed {items:{item_id}} shells); N4d 2026-10-02 (coordinator Codex Round 13, P1): the classification
            // side was compared RAW — a shell never equals the inventory's scalar classification, so the _not_active gate stayed TRUE with the
            // weapon equipped (the feat bonus granted wrongly) and the _active gate could wrongly refuse
            const conditions = entries(ability, key).map(v => family === 'weapon_type' || family === 'weapon_classification' ? readLayerListEntryUuid(v) : v);
            // …and the INVENTORY row's classification value too (PHP: layer_list_entry_uuid(layer_value($inventory_value, 'weapon_classification')), :238 / :287)
            const classification = (row: ItemFieldRow): string => readLayerListEntryUuid(readers.layerValue(row, 'weapon_classification'));
            let done = false;
            for (const condition of conditions) {
                if (family === 'weapon_has_property' && (typeof condition !== 'string' || condition === '')) continue;
                if (!has(host, 'inventory') || (family === 'weapon_classification' && !negative && met)) continue;
                for (const value of entries(host, 'inventory')) {
                    if (!arrayLike(value)) throw new TypeError('Inventory condition row requires a PHP array');
                    if (phpJsonInt(readInventoryField(value, 'inventory_private') ?? 0) === 2) continue;
                    let match: boolean;
                    if (family === 'armor_type') match = equal(equipped(value), deps.equipmentSlots.armor) && has(value, family)
                        && (negative ? !equal(readers.layerValue(value, family), condition) : equal(readers.layerValue(value, family), condition));
                    else if (family === 'weapon_classification' && !negative) match = has(value, family) && equal(classification(value), condition) && hands(value);
                    else if (family === 'weapon_has_property') match = hands(value) && has(value, condition as string);
                    else match = hands(value) && (family === 'weapon_type' && negative ? has(value, family) : readers.layerValue(value, family) !== null)
                        && equal(family === 'weapon_classification' ? classification(value) : readers.layerValue(value, family), condition);
                    if (match) { met = !negative; done = family === 'weapon_classification'; break; }
                }
                if (done) break;
            }
        }
        for (const negative of [false, true]) {
            const key = 'if_caster_has_status_effect_' + (negative ? 'not_active' : 'active');
            if (!has(ability, key)) continue;
            met = negative;
            for (const id of entries(ability, key)) {
                let match = false;
                if (deps.vitalityStatusIds.some(v => v === id)) {
                    const raw = readers.computedValue(host, 'vitality', 'v_status');
                    const status = raw == null || raw === '' ? '' : phpJsonString(arrayLike(raw) ? own(own(raw, 'items'), 'item_id') ?? own(raw, '0') ?? '' : raw);
                    match = status === id;
                } else if (phpJsonTruthy(readCreatureStatuses(host, 'target', reference))) {
                    for (const row of Object.values(readCreatureStatuses(host, 'target', reference))) {
                        if (readers.itemField(row as ItemFieldRow, 'item_id') === id) { match = true; break; }
                    }
                }
                if (match) { met = !negative; break; }
            }
        }
        let light: unknown = null;
        if (has(ability, 'if_light_type_active') || has(ability, 'if_light_type_not_active')) light = source('present-light', [host]);
        for (const negative of [false, true]) {
            const key = 'if_light_type_' + (negative ? 'not_active' : 'active');
            if (!has(ability, key) || light === null) continue;
            if (!arrayLike(light)) throw new TypeError('Present light must be a PHP array');
            const match = entries(ability, key).some(e => Object.values(light).some(v => v === readLayerListEntryUuid(e)));
            met = negative ? met && !match : match;
        }
        let environment: unknown = null;
        if (has(ability, 'if_environment_active') || has(ability, 'if_environment_not_active')) {
            let raw = own(own(host, 'encounter_creatures'), 'encounter_environment') ?? null;
            if (arrayLike(raw)) raw = own(own(raw, 'items'), 'item_id') ?? own(raw, '0') ?? null;
            if (typeof raw !== 'string' || raw === '') raw = readers.layerValue(host, 'encounter_environment') ?? readers.layerValue(host, 'location_environment') ?? null;
            environment = typeof raw === 'string' && raw !== '' ? raw : null;
        }
        for (const negative of [false, true]) {
            const key = 'if_environment_' + (negative ? 'not_active' : 'active');
            if (has(ability, key) && environment !== null) {
                const match = entries(ability, key).some(v => equal(environment, v)); met = negative ? !match : match;
            }
        }
        let weather: unknown = null;
        if (has(ability, 'if_weather_active') || has(ability, 'if_weather_not_active')) {
            let raw = own(own(host, 'encounter_creatures'), 'encounter_weather') ?? null;
            if (arrayLike(raw)) raw = own(own(raw, 'items'), 'item_id') ?? own(raw, '0') ?? null;
            if (typeof raw !== 'string' || raw === '') raw = readers.layerValue(host, 'encounter_weather') ?? readers.layerValue(host, 'location_weather') ?? null;
            weather = typeof raw === 'string' && raw !== '' ? raw : null;
        }
        // Weather must preserve every earlier condition failure; unknown context still skips.
        for (const negative of [false, true]) {
            const key = 'if_weather_' + (negative ? 'not_active' : 'active');
            if (has(ability, key) && weather !== null) {
                const match = entries(ability, key).some(v => equal(weather, readLayerListEntryUuid(v))); met = met && (negative ? !match : match);
            }
        }
        for (const negative of [false, true]) {
            const key = 'if_effective_light_type_' + (negative ? 'not_active' : 'active');
            if (has(ability, key)) {
                const effective = source('effective-light', [host]);
                if (effective !== null) { const match = entries(ability, key).some(v => readLayerListEntryUuid(v) === effective); met = met && (negative ? !match : match); }
            }
        }
        for (const [family, kind] of [['outdoors', 'outdoors'], ['sunlight', 'sunlight']] as const) for (const negative of [false, true]) {
            if (has(ability, 'if_' + family + '_' + (negative ? 'not_active' : 'active'))) {
                const answer = source(kind, [host]); if (answer !== null) met = met && answer === !negative;
            }
        }
        if (has(ability, 'if_caster_summon_within_ft')) {
            const feet = phpJsonFloat(readers.layerValue(ability, 'if_caster_summon_within_ft'));
            const answer = source('summon-within', [host, feet]);
            if (answer !== null) met = met && answer === true;
        }
        if (met && has(ability, 'requires_equipped_item')) met = phpJsonTruthy(source('equipped-items', [host, ability]));
        if (phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability'))
            && phpJsonTruthy(readers.layerValue(ability, '_draw_bound_inventory_id')) && !template && met) {
            if (!phpJsonTruthy(source('draw-sheathed', [host, readers.layerValue(ability, '_draw_bound_inventory_id')]))) met = false;
        }
        if (met && !deferSkill) met = phpJsonTruthy(source('skills', [ability, host]));
        if (met && readers.layerValue(ability, 'ability_active_if_ability_charge_is_at_median') !== null) {
            const id = phpJsonString(readers.layerValue(ability, 'ability_active_if_ability_charge_is_at_median'));
            met = valid(id) && phpJsonTruthy(source('median-charge', [host, id]));
        }
        return met;
    }
    return {hasRuntimeConditions, check};
}
